<script setup lang="ts">
  import type { ButtonBranding as ButtonBrandingType } from '~/types/branding'
  import type { ReferralProgram } from '~/types/referral'

  const props = defineProps<{
    referralProgram: ReferralProgram
    isExtensionMode?: boolean
  }>()

  const { getRewardFormattedTextWithType } = useFormattedText()
  const { isAuthenticated } = storeToRefs(useStoreAuth())
  const isSuccess = ref(false)
  const showModal = ref(false)

  const userUnauthenticatedEmail = ref('')
  const userUnauthenticatedReferralId = ref(0)

  const section = computed(() => {
    const { brandings } = useStoreBranding()

    if (!brandings?.branding_referral) return

    const { extractTypography } = useBrandingTypography()
    const { extractButton } = useBrandingButton()

    const { id, section_color, layout, img_desktop, img_mobile } = brandings.branding_referral

    return {
      id,
      backgroundImgDesktop: img_desktop,
      backgroundImgMobile: img_mobile,
      card: extractBrandingCard(brandings.branding_referral.card),
      contentAlignment: getContentAlignmentResponsive(layout),
      backgroundColor: section_color,
      title: extractTypography('branding_referral', 'title', 'primary'),
      titleUnauthenticated: extractTypography('branding_referral', 'title_unauthenticated', 'primary'),
      titleSuccess: extractTypography('branding_referral', 'title_success', 'primary'),
      description: extractTypography('branding_referral', 'description', 'secondary'),
      descriptionUnauthenticated: extractTypography('branding_referral', 'description_unauthenticated', 'secondary'),
      descriptionSuccess: extractTypography('branding_referral', 'description_success', 'secondary'),
      actionBtnSuccess: extractButton('branding_referral', 'action_btn_success'),
      actionBtnUnauthenticated: extractButton('branding_referral', 'action_btn_unauthenticated') as ButtonBrandingType
    }
  })

  const isAuthenticatedOrUnauthenticatedEmail = computed(() => isAuthenticated.value || userUnauthenticatedEmail.value)

  const referralTitle = computed(() => {
    if (!section.value) return
    if (isSuccess.value) return section.value.titleSuccess.text

    const { amount: godsonAmout } = props.referralProgram.godson_reward
    const { amount: godfatherAmount } = props.referralProgram.godfather_reward
    const godsonDynamicText = isAuthenticatedOrUnauthenticatedEmail.value
      ? 'local_currency_referral_friend'
      : 'reward_amount_friend'
    const godfatherDynamicText = isAuthenticatedOrUnauthenticatedEmail.value
      ? 'local_currency_reward_customer'
      : 'reward_amount_customer'
    const titleText = isAuthenticatedOrUnauthenticatedEmail.value
      ? section.value.title.text
      : section.value.titleUnauthenticated.text

    let rewardText = getRewardFormattedTextWithType(
      titleText,
      godsonAmout,
      godsonDynamicText,
      props.referralProgram.godson_reward.type
    )
    rewardText = getRewardFormattedTextWithType(
      rewardText,
      godfatherAmount,
      godfatherDynamicText,
      props.referralProgram.godfather_reward.type
    )
    return rewardText
  })

  const referralDescription = computed(() => {
    if (!section.value) return

    if (isSuccess.value) return section.value.descriptionSuccess.text

    return isAuthenticatedOrUnauthenticatedEmail.value
      ? section.value.description.text
      : section.value.descriptionUnauthenticated.text
  })

  const onCanRefer = (response: { email: string; referralId?: number }) => {
    userUnauthenticatedEmail.value = response.email
    userUnauthenticatedReferralId.value = response.referralId!
  }
</script>

<template>
  <SectionAnchored anchor-id="referral">
    <SectionReferral
      v-if="section"
      id="referral"
      class="referral-wrapper"
      :section-card-disposition="section.contentAlignment"
      :background-color="section.backgroundColor"
      :background-image-desktop-url="section.backgroundImgDesktop"
      :background-image-mobile-url="section.backgroundImgMobile"
      :border-radius="section.card.borderRadius"
      :border-color="section.card.borderColor"
      :hover-background-color="section.card.hoverBackgroundColor"
      :section-card="section.card"
      :section-title="section.title"
      :section-description="section.description"
    >
      <template #background-image="{ imageUrl }">
        <NuxtImg
          :src="imageUrl"
          format="webp"
          alt="Background image of the referral card"
          loading="lazy"
        />
      </template>
      <template #title><span v-dompurify-html="referralTitle" /></template>
      <template #description>{{ referralDescription }}</template>
      <div class="referral-wrapper__content">
        <template v-if="isAuthenticated || (!isAuthenticated && userUnauthenticatedEmail)">
          <LazySectionReferralTabs
            v-if="!isSuccess"
            :is-extension-mode="isExtensionMode"
            :user-unauthenticated-email="userUnauthenticatedEmail"
            :user-unauthenticated-referral-id="userUnauthenticatedReferralId"
            @success="isSuccess = true"
          />
          <ButtonBranding
            v-else
            class="referral-form--restart"
            :button-branding="section.actionBtnSuccess"
            @click="isSuccess = false"
          >
            {{ section.actionBtnSuccess.text }}
          </ButtonBranding>

          <a
            v-if="isAuthenticated"
            class="referral-wrapper--link text-secondary-heading-2"
            @click="showModal = true"
          >
            {{ $t('SectionReferral.seeReferral') }}
          </a>
          <SectionReferralModal
            v-if="showModal"
            :is-extension-mode="isExtensionMode"
            @close="showModal = false"
          />
        </template>
        <SectionReferralNotAuthenticatedForm
          v-else
          :button-branding="section.actionBtnUnauthenticated"
          @can-refer="onCanRefer"
        />
      </div>
    </SectionReferral>
  </SectionAnchored>
</template>

<style scoped>
  .referral-wrapper {
    display: flex;
    aspect-ratio: auto;

    @media (min-width: 768px) {
      aspect-ratio: 2.02 / 1;
    }

    &__content {
      display: flex;
      flex-direction: column;
      gap: 10px;
      @media (min-width: 768px) {
        gap: 20px;
      }
    }

    &--link {
      text-decoration: underline;
      text-align: center;
      display: block;
      cursor: pointer;
      color: v-bind(section.description.color);
    }
  }
</style>
